<template>
  <component
    :is="tag"
    class="app-icon-button"
    :class="rootClasses"
    v-bind="elementAttrs"
    :title="title || null"
    :aria-label="ariaLabel"
    v-on="eventListeners"
  >
    <i class="app-icon-button__icon" :class="iconClass" aria-hidden="true"></i>
    <span v-if="label" class="app-icon-button__label">{{ label }}</span>
  </component>
</template>

<script>
/**
 * Compact row-action button — the small icon (or icon + short label) controls
 * used in table action cells: edit, change-password, delete, "login as" links.
 *
 * Renders as `<button>` by default, `<a>` when `href` is set, or `<router-link>`
 * when `to` is set — so the same component covers both real navigations
 * (login-as links) and in-page actions (open modal). Idle state is muted gray;
 * the tone color surfaces on hover, keeping a row of actions visually quiet
 * until the user reaches for one.
 *
 * Stable classes (`app-icon-button`, `--labeled`, `__label`) are part of the
 * contract: consumers reach them via `:deep()` for responsive tweaks (e.g.
 * collapsing labeled buttons to icon-only on narrow screens).
 */
const TONES = ['neutral', 'primary', 'purple', 'teal', 'warning', 'danger']

export default {
  name: 'AppIconButton',

  inheritAttrs: false,

  props: {
    // FA class. Auto-prefixed with `fa-regular` unless a full class string
    // (containing a space, e.g. "fa-solid fa-circle") is passed.
    icon: {
      type: String,
      required: true,
    },
    // Optional short label — switches to the wider "labeled" variant.
    label: {
      type: String,
      default: '',
    },
    tone: {
      type: String,
      default: 'neutral',
      validator: (v) => TONES.includes(v),
    },
    // Link modes. `to` → router-link, `href` → anchor; otherwise a button.
    href: {
      type: String,
      default: '',
    },
    to: {
      type: [String, Object],
      default: '',
    },
    target: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click'],

  computed: {
    tag() {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return 'button'
    },

    rootClasses() {
      return [
        `app-icon-button--${this.tone}`,
        {
          'app-icon-button--labeled': !!this.label,
          'app-icon-button--disabled': this.disabled,
        },
      ]
    },

    iconClass() {
      return this.icon.includes(' ') ? this.icon : `fa-regular ${this.icon}`
    },

    // Icon-only buttons need an accessible name; fall back to the label when
    // no explicit title was given.
    ariaLabel() {
      // aria-label attr: icon-only buttons wrapped in app-tooltip skip `title` to avoid a second, native tooltip
      return this.title || this.label || this.$attrs['aria-label'] || null
    },

    elementAttrs() {
      const attrs = { ...this.$attrs }
      if (this.tag === 'button') {
        attrs.type = 'button'
        attrs.disabled = this.disabled || undefined
      }
      if (this.tag === 'a') {
        attrs.href = this.disabled ? null : this.href
        if (this.target) {
          attrs.target = this.target
          // Security default for new-tab links.
          if (this.target === '_blank') attrs.rel = attrs.rel || 'noopener'
        }
      }
      if (this.tag === 'router-link') {
        attrs.to = this.disabled ? '' : this.to
      }
      return attrs
    },

    eventListeners() {
      return {
        ...this.$listeners,
        click: (e) => {
          if (this.disabled) {
            e.preventDefault()
            return
          }
          this.$emit('click', e)
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — keep the component portable (no theme import).
$ib-muted:  #6c7a89; // idle icon color
$ib-text:   #2b3a4a; // neutral hover color
$ib-soft:   #f1f3f5; // neutral hover background
$ib-radius: 6px;

.app-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: $ib-radius;
  color: $ib-muted;
  cursor: pointer;
  font-size: 13px;
  font-family: inherit;
  text-decoration: none;
  transition: background-color 120ms ease, color 120ms ease;

  &:focus-visible {
    outline: 2px solid #1f73c1;
    outline-offset: 1px;
  }

  // ── Labeled variant (icon + short text) ──
  &--labeled {
    width: auto;
    padding: 0 10px;
    gap: 5px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
  }

  &__label { line-height: 1; }

  // ── Disabled ──
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }

  // ── Tone hover variants ──
  // Idle is always muted gray; the tone reveals on hover so a row of actions
  // stays visually quiet until interacted with.
  &--neutral:hover {
    background: $ib-soft;
    color: $ib-text;
  }
  &--primary:hover {
    background: rgba(31, 115, 193, 0.10);
    color: #1f73c1;
  }
  &--purple:hover {
    background: rgba(109, 70, 193, 0.10);
    color: #6d46c1;
  }
  &--teal:hover {
    background: rgba(14, 165, 150, 0.10);
    color: #0ea596;
  }
  &--warning:hover {
    background: rgba(196, 128, 28, 0.10);
    color: #c4801c;
  }
  &--danger:hover {
    background: rgba(207, 34, 46, 0.08);
    color: #cf222e;
  }
}
</style>
